import * as THREE from "three";
import type { IkChain, IkKinematicsState } from "./types";

const toVector3 = (v?: number[]): THREE.Vector3 => {
  if (!Array.isArray(v) || v.length < 3) return new THREE.Vector3();
  return new THREE.Vector3(v[0], v[1], v[2]);
};

const toQuaternion = (q?: number[]): THREE.Quaternion => {
  if (!Array.isArray(q) || q.length < 4) return new THREE.Quaternion(1, 0, 0, 0);
  // Stored as [w, x, y, z]
  return new THREE.Quaternion(q[1], q[2], q[3], q[0]).normalize();
};

export function computeKinematics(chain: IkChain, jointValues: number[]): IkKinematicsState {
  const linkStates: IkKinematicsState["linkStates"] = [];

  // Root pose
  let currentPos = toVector3(chain.rootTransform.position);
  let currentRot = toQuaternion(chain.rootTransform.orientation);

  chain.links.forEach((link) => {
    const value =
      link.kind === "hinge" || link.kind === "slide"
        ? (jointValues[link.dofIndex ?? -1] ?? link.reference ?? 0)
        : 0;

    // Mirror the viewer kinematics semantics (see `src/viewer/joints/hingeJoint.js`,
    // `src/viewer/joints/slideJoint.js`):
    // - `bodyOffsetPosition` / `bodyOffsetOrientation` define the child body's pose in the
    //   parent frame at q=0.
    // - `anchor` (joint.position) is specified in the CHILD body-local frame.
    // - `axis` is specified in the CHILD body-local frame.
    // - The joint acts about a pivot located at: t0 + (R0 * jointPosLocal) in the parent frame.
    const t0Parent = toVector3(link.bodyOffsetPosition);
    const R0Parent = toQuaternion(link.bodyOffsetOrientation);

    const jointPosLocal = toVector3(link.anchor);
    const axisLocal = toVector3(link.axis).normalize();

    // Joint pivot expressed in the parent frame at q=0
    const jointOffsetParent = jointPosLocal.clone().applyQuaternion(R0Parent);
    const pivotPosParent = t0Parent.clone().add(jointOffsetParent);

    // Joint axis expressed in the parent frame (constant direction for hinge about itself)
    const axisParent = axisLocal.clone().applyQuaternion(R0Parent).normalize();

    // Joint pivot/axis in world (model) coordinates
    const anchorWorld = pivotPosParent.clone().applyQuaternion(currentRot).add(currentPos);
    const axisWorld = axisParent.clone().applyQuaternion(currentRot).normalize();

    let nextPos: THREE.Vector3;
    let nextRot: THREE.Quaternion;
    let axisWorldForJac = axisWorld;
    let anchorWorldForJac = anchorWorld;

    if (link.kind === "fixed") {
      // Fixed transform segment: just apply the child body's zero pose.
      nextPos = t0Parent.clone().applyQuaternion(currentRot).add(currentPos);
      nextRot = currentRot.clone().multiply(R0Parent);
      axisWorldForJac = new THREE.Vector3(0, 0, 1).applyQuaternion(currentRot).normalize();
      anchorWorldForJac = currentPos.clone();
    } else {
      // Joint rotation in the PARENT frame (viewer rotates the pivot group in parent space)
      const jointRotParent =
        link.kind === "hinge"
          ? new THREE.Quaternion().setFromAxisAngle(axisParent, value)
          : new THREE.Quaternion();

      // Child origin position in parent frame (matches pivot-group trick in the viewer)
      const childPosParent = (() => {
        const zeroOffset = jointOffsetParent.clone().negate(); // body origin relative to pivot at q=0
        if (link.kind === "hinge") {
          return pivotPosParent.clone().add(zeroOffset.applyQuaternion(jointRotParent));
        }
        // slide: translate along axis in parent frame
        return pivotPosParent.clone().add(zeroOffset).add(axisParent.clone().multiplyScalar(value));
      })();

      nextPos = childPosParent.clone().applyQuaternion(currentRot).add(currentPos);

      // Child/world orientation
      nextRot =
        link.kind === "hinge"
          ? currentRot.clone().multiply(jointRotParent).multiply(R0Parent)
          : currentRot.clone().multiply(R0Parent);
    }

    linkStates.push({
      axisWorld: axisWorldForJac,
      anchorWorld: anchorWorldForJac,
      rotation: nextRot,
      position: nextPos,
    });

    currentPos = nextPos;
    currentRot = nextRot;
  });

  let endEffectorPos = currentPos.clone();
  if (Array.isArray(chain.endEffectorOffset) && chain.endEffectorOffset.length >= 3) {
    const offsetLocal = toVector3(chain.endEffectorOffset);
    endEffectorPos = endEffectorPos.add(offsetLocal.applyQuaternion(currentRot));
  }
  const endEffector: IkKinematicsState["endEffector"] = {
    position: endEffectorPos,
    rotation: currentRot.clone(),
  };

  return { linkStates, endEffector };
}
